import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Stack, Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PieChart } from '@mui/icons-material';
import api from '../../services/api';
import type { FiscalSummary, MonotributoSummary, VatSummary } from '../../types';
import TextField from '../common/TextField';

/**
 * Resumen fiscal de lo facturado con CAE: margen hasta el tope del monotributo (torta, 12 meses, proyección y
 * recategorización) o, para Responsable Inscripto, débito − crédito fiscal = IVA del mes.
 */

const money = (n: number) => n.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
const monthLabel = (ym: string, opts: Intl.DateTimeFormatOptions = { month: 'short' }) =>
  new Date(`${ym}-15T12:00:00`).toLocaleDateString('es-AR', opts);
const parseAmount = (v: string) => Number(v.replace(/\./g, '').replace(',', '.')) || 0;
const errorOf = (e: any, fallback: string) => e?.response?.data?.message || fallback;

const FiscalSummaryCard: React.FC = () => {
  const [data, setData] = useState<FiscalSummary | null>(null);
  const [hidden, setHidden] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      setData(await api.getFiscalSummary());
    } catch (e: any) {
      // Sin CUIT cargado todavía no hay nada que resumir.
      if (e?.response?.status === 404) setHidden(true);
      else setError(errorOf(e, 'No se pudo cargar el resumen fiscal'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (hidden || (data && !data.monotributo && !data.vat)) return null;

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <PieChart color="primary" />
          <Typography variant="h6">{data?.vat ? 'IVA del mes' : 'Tope del monotributo'}</Typography>
        </Stack>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {!data ? (!error && <Typography variant="body2" color="text.secondary">Cargando…</Typography>)
          : data.monotributo ? <Monotributo s={data.monotributo} onSaved={load} onError={setError} />
          : data.vat ? <Vat s={data.vat} onSaved={load} onError={setError} />
          : null}
      </CardContent>
    </Card>
  );
};

const LEVEL_COLOR = { ok: '#84cc16', warning: '#f59e0b', danger: '#f97316', exceeded: '#ef4444' } as const;
const LEVEL_SEVERITY = { ok: 'success', warning: 'warning', danger: 'warning', exceeded: 'error' } as const;

const Donut: React.FC<{ percent: number; color: string; children: React.ReactNode }> = ({ percent, color, children }) => {
  const theme = useTheme();
  const r = 70; const c = 2 * Math.PI * r; const used = Math.min(100, Math.max(0, percent));
  return (
    <Box sx={{ position: 'relative', width: 190, height: 190, flexShrink: 0 }}>
      <svg viewBox="0 0 180 180" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
        <circle cx="90" cy="90" r={r} fill="none" stroke={theme.palette.divider} strokeWidth="22" />
        <circle cx="90" cy="90" r={r} fill="none" stroke={color} strokeWidth="22" strokeDasharray={`${(used / 100) * c} ${c}`} />
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        {children}
      </Box>
    </Box>
  );
};

type Props<T> = { s: T; onSaved: () => void; onError: (m: string) => void };

const Monotributo: React.FC<Props<MonotributoSummary>> = ({ s, onSaved, onError }) => {
  const [month, setMonth] = useState(s.months[s.months.length - 1]?.month ?? '');
  const [external, setExternal] = useState('');
  const max = Math.max(1, ...s.months.map((m) => m.total));
  const save = async () => {
    try { await api.saveFiscalPeriod(month, { externalBilled: parseAmount(external) }); setExternal(''); onSaved(); }
    catch (e) { onError(errorOf(e, 'No se pudo guardar')); }
  };
  const stat = (label: string, value: string, sub?: string, color?: string) => (
    <Grid item xs={6} md={3}><Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography fontWeight={700} color={color}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper></Grid>
  );
  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center">
        <Donut percent={s.usedPercent ?? 0} color={LEVEL_COLOR[s.level]}>
          {s.annualCap ? (<><Typography variant="h4" fontWeight={800} sx={{ color: LEVEL_COLOR[s.level] }}>{(s.usedPercent ?? 0).toLocaleString('es-AR', { maximumFractionDigits: 1 })}%</Typography>
            <Typography variant="caption" color="text.secondary">del tope anual</Typography></>)
            : <Typography variant="caption" color="text.secondary" px={4}>Elegí tu categoría arriba y guardá</Typography>}
        </Donut>
        <Box flex={1} width="100%">
          <Typography variant="subtitle1" fontWeight={700}>Monotributo {s.category ? `— categoría ${s.category}` : ''}
            {s.annualCap && <Typography component="span" variant="body2" color="text.secondary"> · tope {money(s.annualCap)} en 12 meses</Typography>}</Typography>
          <Grid container spacing={1.5} my={1}>
            {stat('Facturado (12 meses)', money(s.billedLast12Months))}
            {stat('Te queda', s.remaining != null ? money(Math.max(0, s.remaining)) : '—', undefined, s.remaining != null && s.remaining < 0 ? 'error.main' : 'success.main')}
            {stat('Promedio mensual', money(s.monthlyAverage))}
            {stat('Al ritmo actual', money(s.projectedAnnual), s.categoryForProjection ? `categoría ${s.categoryForProjection}` : 'supera la K')}
          </Grid>
          <Alert severity={LEVEL_SEVERITY[s.level]}>{s.advice}</Alert>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Próxima recategorización: {monthLabel(s.nextRecategorization.slice(0, 7), { month: 'long', year: 'numeric' })}.
            {s.tableValidFrom && ` Topes de ARCA vigentes desde ${new Date(`${s.tableValidFrom}T12:00:00`).toLocaleDateString('es-AR')}.`}
          </Typography>
        </Box>
      </Stack>

      <Box>
        <Typography fontWeight={700} mb={2}>Últimos 12 meses</Typography>
        <Stack direction="row" alignItems="flex-end" spacing={0.75} sx={{ height: 150 }}>
          {s.months.map((m) => (
            <Stack key={m.month} flex={1} alignItems="center" justifyContent="flex-end" sx={{ height: '100%' }} title={`${m.month}: ${money(m.total)}`}>
              <Box sx={{ width: '100%', height: `${(m.total / max) * 100}%`, minHeight: m.total ? 3 : 0, display: 'flex', flexDirection: 'column', borderRadius: '4px 4px 0 0', overflow: 'hidden' }}>
                <Box sx={{ bgcolor: 'info.main', height: `${m.total ? (m.external / m.total) * 100 : 0}%` }} />
                <Box sx={{ bgcolor: 'success.main', flex: 1 }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{monthLabel(m.month)}</Typography>
            </Stack>
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary">Verde: facturado desde DashFlow · Azul: facturado por fuera</Typography>
      </Box>

      <Box>
        <Typography fontWeight={700}>¿Facturaste también por fuera?</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>Si hiciste facturas desde otro sistema o desde la web de ARCA, cargalas por mes para que el tope sea exacto.</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Mes</InputLabel>
            <Select label="Mes" value={month} onChange={(e) => setMonth(e.target.value)}>
              {[...s.months].reverse().map((m) => <MenuItem key={m.month} value={m.month}>{monthLabel(m.month, { month: 'long', year: 'numeric' })}{m.external ? ` (${money(m.external)})` : ''}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label="Facturado por fuera" value={external} onChange={(e) => setExternal(e.target.value)} inputProps={{ inputMode: 'decimal' }} />
          <Button variant="outlined" onClick={save}>Guardar</Button>
        </Stack>
      </Box>
    </Stack>
  );
};

const Vat: React.FC<Props<VatSummary>> = ({ s, onSaved, onError }) => {
  const [credit, setCredit] = useState<Record<string, string>>({});
  const save = async (m: string) => {
    try { await api.saveFiscalPeriod(m, { vatCredit: parseAmount(credit[m] ?? '') }); setCredit((c) => ({ ...c, [m]: '' })); onSaved(); }
    catch (e) { onError(errorOf(e, 'No se pudo guardar')); }
  };
  const card = (title: string, m: VatSummary['current']) => (
    <Grid item xs={12} md={6}><Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography fontWeight={700} sx={{ textTransform: 'capitalize' }}>{title} · {monthLabel(m.month, { month: 'long', year: 'numeric' })}</Typography>
      <Typography variant="caption" color="text.secondary">Facturado {money(m.invoicedTotal)}</Typography>
      <Stack spacing={1} mt={2}>
        <Stack direction="row" justifyContent="space-between"><span>Débito fiscal (IVA de tus facturas)</span><b>{money(m.debitoFiscal)}</b></Stack>
        <Stack direction="row" justifyContent="space-between"><span>Crédito fiscal (IVA de tus compras)</span><b>− {money(m.creditoFiscal)}</b></Stack>
        <Stack direction="row" justifyContent="space-between" sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}>
          <span>{m.neto >= 0 ? 'IVA a pagar' : 'Saldo a favor'}</span>
          <Typography variant="h6" fontWeight={800} color={m.neto > 0 ? 'warning.main' : 'success.main'}>{money(Math.abs(m.neto))}</Typography>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={1} mt={2}>
        <TextField size="small" fullWidth label="Crédito fiscal del mes" value={credit[m.month] ?? ''} onChange={(e) => setCredit((c) => ({ ...c, [m.month]: e.target.value }))} />
        <Button variant="outlined" onClick={() => save(m.month)}>Guardar</Button>
      </Stack>
    </Paper></Grid>
  );
  return (
    <Stack spacing={2}>
      <Alert severity={s.previous.neto > 0 ? 'warning' : 'success'}>{s.advice}</Alert>
      <Grid container spacing={2}>{card('Mes pasado', s.previous)}{card('Mes en curso', s.current)}</Grid>
      <Typography variant="caption" color="text.secondary">Es orientativo: el monto final lo confirma tu contador en la declaración jurada.</Typography>
    </Stack>
  );
};

export default FiscalSummaryCard;
